import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Coffee } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const EmailVerificationScreen = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | success | expired | already_used | invalid

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('invalid');
      return;
    }

    supabase.functions
      .invoke('confirm-verification-email', { body: { token } })
      .then(({ data, error }) => {
        if (error || !data) {
          setStatus('invalid');
          return;
        }
        if (data.success) {
          setStatus('success');
        } else {
          const KNOWN_STATUSES = ['expired', 'already_used', 'invalid'];
          const raw = data.error ?? 'invalid';
          setStatus(KNOWN_STATUSES.includes(raw) ? raw : 'invalid');
        }
      });
  }, [searchParams]);

  const messages = {
    success: {
      icon: <CheckCircle size={48} className="text-white" />,
      iconBg: 'var(--brand-primary)',
      title: "You're Verified!",
      body: 'Your DSP status has been confirmed. Welcome to the community.',
      cta: { label: 'Open DSP Cafe', to: '/feed' },
    },
    expired: {
      icon: <XCircle size={48} className="text-white" />,
      iconBg: '#D97706',
      title: 'Link Expired',
      body: 'This verification link has expired. Request a new one from the Verify screen inside the app.',
      cta: { label: 'Back to DSP Cafe', to: '/feed' },
    },
    already_used: {
      icon: <CheckCircle size={48} className="text-white" />,
      iconBg: '#6B7280',
      title: 'Already Used',
      body: 'This verification link has already been used. If you need help, contact hello@dsp.cafe.',
      cta: { label: 'Back to DSP Cafe', to: '/feed' },
    },
    invalid: {
      icon: <XCircle size={48} className="text-white" />,
      iconBg: '#DC2626',
      title: 'Invalid Link',
      body: 'This verification link is not valid. If you think this is a mistake, contact hello@dsp.cafe.',
      cta: { label: 'Back to DSP Cafe', to: '/feed' },
    },
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ backgroundColor: 'var(--bg-cream)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 mb-10">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          <Coffee size={20} className="text-white" />
        </div>
        <span className="font-bold text-lg" style={{ color: 'var(--brand-primary)' }}>DSP Cafe</span>
      </div>

      <div
        className="w-full max-w-sm rounded-2xl p-8 text-center"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
      >
        {status === 'loading' ? (
          <>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--pill-bg)' }}>
              <span className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: 'var(--brand-primary)' }} />
            </div>
            <h2 className="text-lg font-bold text-primary mb-2">Verifying...</h2>
            <p className="text-sm text-tertiary">Just a moment while we confirm your email.</p>
          </>
        ) : (
          <>
            <div
              className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: messages[status].iconBg }}
            >
              {messages[status].icon}
            </div>
            <h2 className="text-xl font-bold text-primary mb-2">{messages[status].title}</h2>
            <p className="text-sm text-tertiary mb-6">{messages[status].body}</p>
            <Link
              to={messages[status].cta.to}
              className="inline-block w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {messages[status].cta.label}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationScreen;
